import { io, Socket } from "socket.io-client";
import EventEmitter from "eventemitter3";
import { ConveyApi } from "./ConveyApi";
import { getStore, getStoreState } from "./reduxHelpers";
import { setConversationEntity } from "@actions";
import {
  conversationNeedsHydration,
  extractConversationEventContext,
  isForeignAgencyEvent,
  parseSipChannelEvent,
  sipEventName
} from "./liveShareConversation";

interface IConveySocketOpts {
  correlationId: string;
  agencyId: string;
}

export class ConveySocket extends EventEmitter {
  private static instance: ConveySocket;
  static socketManager?: Socket;
  static correlationId: string;
  static agencyId: string;
  static SIP_EVENT_NAMES: Set<string> = new Set<string>();
  static LISTENED_EVENTS: string[] = [];
  static pendingLiveShareFetches: Set<string> = new Set<string>();
  static JOINED_SIP_AUDIO: Set<string> = new Set<string>();

  static ERROR = "error";
  static CONNECT = "connect";
  static CONNECTED = "connected";
  static CONNECTING = "connecting";
  static RECONNECT = "reconnect";
  static RECONNECTING = "reconnecting";
  static RECONNECTED = "reconnected";
  static DISCONNECT = "disconnect";
  static DISCONNECTED = "disconnected";
  static CONNECT_ERR = "connect_error";
  static RECONNECT_ERR = "reconnect_error";

  static SESSION = "sessionCreated";
  static AGENCY_CONNECTED = "agencyConnected";
  static CREATE_MESSAGE = "createMessage";
  // A stored message changed after it was created - notably its translation
  // (body_transl / language), which the API fills in asynchronously.
  static UPDATE_MESSAGE = "updateMessage";
  static CREATE_VIDEO_ROOM = "createVideoRoom";
  static CLOSE_VIDEO_ROOM = "closeVideoRoom";
  static ADD_RECIPIENT_GEOLOCATION = "addRecipientGeolocation";
  static ADD_RECIPIENT_CONVERSATION = "addRecipientToConversation";
  static UPDATE_CONVERSATION_RECIPIENT = "updateConversationRecipient";
  static UPDATE_VIDEO_STATUS = "updateVideoStatus";
  static CREATE_ATTACHMENT = "createAttachment";
  static UPDATE_ATTACHMENT = "updateAttachment";
  static UPDATE_MESSAGE_STATUS = "updateMessageStatus";
  static RECEIVE_TRANSCRIPTION = "receive-transcription";
  static INTERPRETATION = "interpretation";
  static SIP_RELAY = "sip:relay";
  static RECEIVE_VIDEO_TRANSCRIPTION = "sdk:receive-transcription";
  static RECEIVE_SHARE_INFORMATION = "receive-share-information";
  static TRANSCRIPTION_CALLBACK = "transcriptionCallback";
  static UPDATE_RECIPIENT_LANGUAGE = "updateRecipientLanguage";
  static ADD_VIDEO_ROOM_PARTICIPANT = "addVideoRoomParticipant";
  static REMOVE_VIDEO_ROOM_PARTICIPANT = "deleteVideoRoomParticipant";
  static START_TRANSCRIPTION = "start-transcription";
  static STOP_TRANSCRIPTION = "stop-transcription";
  static ENABLE_CAMERA = "enableCamera";
  static DISABLE_CAMERA = "disableCamera";
  static ENABLE_AUDIO = "enableAudio";
  static DISABLE_AUDIO = "disableAudio";
  static TOGGLE_CAMERA = "toggleCamera";
  static SHARE_LOCATION = "shareLocation";
  static CHANGE_RECOG_LANG = "changeRecognitionLanguage";
  static VISIBILITY_CHANGE = "visibilityChange";
  static UPLOAD_FILE = "fileUpload";

  // chat: onEvent messages
  static CREATE_CONVERSATION = "createConversation";
  static UPDATE_CONVERSATION = "updateConversation";
  static CLOSE_CONVERSATION = "closeConversation";
  static ASSIGN_CONVERSATION_AGENT = "assignConversationAgent";
  static UNASSIGN_CONVERSATION_AGENT = "unassignConversationAgent";
  static ADD_RECIPIENT_TO_CONVERSATION = "addRecipientToConversation";
  static REMOVE_RECIPIENT_TO_CONVERSATION = "removeRecipientFromConversation";

  static JOIN_CONVERSATION_CMD = "joinConversation";
  static LEAVE_CONVERSATION_CMD = "leaveConversation";
  static JOIN_SIP_AUDIO_CMD = "joinSipAudio";
  static LEAVE_SIP_AUDIO_CMD = "leaveSipAudio";
  static SEND_TRANSCRIPTION_CMD = "send-transcription";
  static CONNECT_AGENCY_FEED_CMD = "connectAgencyFeed";
  static VIDEO_REMOTE_CONTROL_CMD = "remoteControl";
  static VIDEO_TRANSCRIPTION_CMD = "sdk:send-transcription";
  static SHARE_INFORMATION_CMD = "share-information";
  static LEAVE_VIDEO_ROOM_CMD = "leaveVideo";
  static REQUEST_START_TRANSCRIPTION_CMD = "request-start-transcription";
  static REQUEST_STOP_TRANSCRIPTION_CMD = "request-stop-transcription";

  static EVENTS = [
    ConveySocket.SESSION,
    ConveySocket.AGENCY_CONNECTED,
    ConveySocket.CREATE_MESSAGE,
    ConveySocket.UPDATE_MESSAGE,
    ConveySocket.ADD_RECIPIENT_GEOLOCATION,
    ConveySocket.CREATE_VIDEO_ROOM,
    ConveySocket.CLOSE_VIDEO_ROOM,
    ConveySocket.UPDATE_VIDEO_STATUS,
    ConveySocket.CREATE_ATTACHMENT,
    ConveySocket.STOP_TRANSCRIPTION,
    ConveySocket.START_TRANSCRIPTION,
    ConveySocket.UPDATE_ATTACHMENT,
    ConveySocket.RECEIVE_TRANSCRIPTION,
    ConveySocket.RECEIVE_VIDEO_TRANSCRIPTION,
    ConveySocket.RECEIVE_SHARE_INFORMATION,
    ConveySocket.TRANSCRIPTION_CALLBACK,
    ConveySocket.UPDATE_RECIPIENT_LANGUAGE,
    ConveySocket.ADD_VIDEO_ROOM_PARTICIPANT,
    ConveySocket.REMOVE_VIDEO_ROOM_PARTICIPANT,
    ConveySocket.ENABLE_CAMERA,
    ConveySocket.DISABLE_CAMERA,
    ConveySocket.ENABLE_AUDIO,
    ConveySocket.DISABLE_AUDIO,
    ConveySocket.TOGGLE_CAMERA,
    ConveySocket.SHARE_LOCATION,
    ConveySocket.CHANGE_RECOG_LANG,
    ConveySocket.VISIBILITY_CHANGE,
    ConveySocket.UPDATE_MESSAGE_STATUS,
    ConveySocket.CREATE_CONVERSATION,
    ConveySocket.UPDATE_CONVERSATION,
    ConveySocket.CLOSE_CONVERSATION,
    ConveySocket.ASSIGN_CONVERSATION_AGENT,
    ConveySocket.UNASSIGN_CONVERSATION_AGENT,
    ConveySocket.ADD_RECIPIENT_TO_CONVERSATION,
    ConveySocket.UPDATE_CONVERSATION_RECIPIENT
  ];

  static CHAT_ON_EVENTS = [
    ConveySocket.CREATE_CONVERSATION,
    ConveySocket.UPDATE_CONVERSATION,
    ConveySocket.CLOSE_CONVERSATION,
    ConveySocket.ASSIGN_CONVERSATION_AGENT,
    ConveySocket.UNASSIGN_CONVERSATION_AGENT,
    ConveySocket.ADD_RECIPIENT_TO_CONVERSATION,
    ConveySocket.UPDATE_CONVERSATION_RECIPIENT
  ];

  static init(accessToken: string) {
    if (!ConveySocket.socketManager) {
      ConveySocket.socketManager = io(process.env.SOCKET_HOST, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 25,
        auth: { token: `Bearer ${accessToken}` }
      });

      ConveySocket.bindSockets();
    }

    return ConveySocket.getInstance();
  }

  static initSip({ agencyId, correlationId }: IConveySocketOpts) {
    const event = ConveySocket.getSipRoom({ agencyId, correlationId });
    ConveySocket.SIP_EVENT_NAMES.add(event);
    const sentimentEvent = ConveySocket.getSipSentiment({ agencyId, correlationId });
    ConveySocket.SIP_EVENT_NAMES.add(sentimentEvent);
    const audioEvent = ConveySocket.getSipAudio({ agencyId, correlationId });
    ConveySocket.SIP_EVENT_NAMES.add(audioEvent);
    const stableEvent = ConveySocket.getSipStable({ agencyId, correlationId });
    ConveySocket.SIP_EVENT_NAMES.add(stableEvent);
    ConveySocket.SIP_EVENT_NAMES.add(ConveySocket.INTERPRETATION);

    if (ConveySocket.socketManager) {
      [event, sentimentEvent, audioEvent, stableEvent, ConveySocket.INTERPRETATION].forEach((eventName) => {
        if (!ConveySocket.LISTENED_EVENTS.includes(eventName)) {
          ConveySocket.socketManager.on(eventName, (data: any) => {
            ConveySocket.getInstance().emit(eventName, data);
          });
          ConveySocket.LISTENED_EVENTS.push(eventName);
        }
      });
    }
    return ConveySocket.getInstance();
  }

  private static bindSockets() {
    if (ConveySocket.socketManager) {
      ConveySocket.socketManager.on(ConveySocket.CONNECT, () => {
        ConveySocket.onConnected();
      });
      ConveySocket.socketManager.on(ConveySocket.RECONNECT, () => {
        ConveySocket.onReconnected();
      });
      ConveySocket.socketManager.on(ConveySocket.DISCONNECT, (reason: string) => {
        const manualDisconnect = reason === "io client disconnect";

        if (manualDisconnect) {
          delete ConveySocket.socketManager;
        }
        ConveySocket.onDisconnected(manualDisconnect);
      });
      ConveySocket.socketManager.on(ConveySocket.RECONNECT_ERR, () => {
        ConveySocket.onError();
      });
      ConveySocket.socketManager.on(ConveySocket.CONNECT_ERR, () => {
        ConveySocket.onError();
      });
      ConveySocket.socketManager.on(ConveySocket.ERROR, () => {
        ConveySocket.onError();
      });
      ConveySocket.socketManager.on(ConveySocket.RECONNECTING, () => {
        ConveySocket.onReconnecting();
      });

      [...ConveySocket.EVENTS].forEach((event) => {
        if (ConveySocket.socketManager) {
          if (!ConveySocket.LISTENED_EVENTS.includes(event)) {
            ConveySocket.socketManager.on(event, (data: any) => {
              ConveySocket.getInstance().emit(event, data);
            });
            ConveySocket.LISTENED_EVENTS.push(event);
          }
        }
      });
      ConveySocket.bindLiveShareHandler();
    }
  }

  private static bindLiveShareHandler() {
    if (!ConveySocket.socketManager) return;
    if (ConveySocket.LISTENED_EVENTS.includes("onAny:liveShare")) return;
    ConveySocket.LISTENED_EVENTS.push("onAny:liveShare");
    ConveySocket.socketManager.onAny((eventName: string, data: any) => {
      ConveySocket.ensureLiveShareConversation(eventName, data);
      ConveySocket.relayLiveShareSipEvent(eventName, data);
    });
  }

  private static relayLiveShareSipEvent(eventName: string, data: any) {
    const parsed = parseSipChannelEvent(eventName);
    if (!parsed) return;
    const selfAgencyId = ConveyApi.agencyId;
    if (!selfAgencyId || parsed.agencyId === selfAgencyId) return;
    const alias = sipEventName(parsed.kind, selfAgencyId, parsed.correlationId);
    if (alias === eventName) return;
    ConveySocket.getInstance().emit(alias, data);
  }

  private static async ensureLiveShareConversation(eventName: string, data: any) {
    const contexts = extractConversationEventContext(eventName, data);
    for (const { conversationId, agencyId } of contexts) {
      if (!isForeignAgencyEvent(agencyId, ConveyApi.agencyId)) continue;
      const existing = getStoreState().conversations?.[conversationId];
      if (existing?.entity && !conversationNeedsHydration(existing.entity)) continue;
      if (ConveySocket.pendingLiveShareFetches.has(conversationId)) continue;

      ConveySocket.pendingLiveShareFetches.add(conversationId);
      try {
        const conversation = await ConveyApi.getConversation({ id: conversationId, agencyId });
        if (!conversation?.conversation_id) continue;
        getStore().dispatch(setConversationEntity(conversation.conversation_id, conversation));
        ConveySocket.getInstance().emit(ConveySocket.CREATE_CONVERSATION, conversation);
      } catch (err) {
        console.error("[ConveySocket:ensureLiveShareConversation] ERROR: ", err);
      } finally {
        ConveySocket.pendingLiveShareFetches.delete(conversationId);
      }
    }
  }
  private static onConnected() {
    ConveySocket.rejoinSipAudio();
    ConveySocket.getInstance().emit(ConveySocket.CONNECTED);
  }

  private static onReconnected() {
    ConveySocket.rejoinSipAudio();
    ConveySocket.getInstance().emit(ConveySocket.CONNECTED);
  }

  getManager() {
    return ConveySocket.socketManager;
  }

  socketEmit(event: string, ...args) {
    if (!ConveySocket.socketManager) {
      return;
    }

    ConveySocket.socketManager.emit(event, ...args);
  }

  private static onDisconnected(manualDisconnect: boolean) {
    // A manual disconnect tears the SDK down, so tracked live-audio rooms must not
    // be silently re-joined for whoever connects next. A transport drop keeps them
    // so the stream resumes on reconnect.
    if (manualDisconnect) {
      ConveySocket.JOINED_SIP_AUDIO.clear();
    }
    ConveySocket.getInstance().emit(ConveySocket.DISCONNECTED, {
      manualDisconnect
    });
  }

  private static onReconnecting() {
    ConveySocket.getInstance().emit(ConveySocket.RECONNECTING);
  }

  private static onError() {
    ConveySocket.getInstance().emit(ConveySocket.ERROR);
  }

  public static joinConversation(conversationId: string) {
    if (!conversationId || !ConveySocket.socketManager) return;
    ConveySocket.socketManager.emit(ConveySocket.JOIN_CONVERSATION_CMD, conversationId);
  }

  public static leaveConversation(conversationId: string) {
    if (!conversationId || !ConveySocket.socketManager) return;
    ConveySocket.socketManager.emit(ConveySocket.LEAVE_CONVERSATION_CMD, conversationId);
  }

  /**
   * Live call audio is published to a dedicated room per call instead of the
   * tenant wide agency feed, so the browser only receives the stream while it has
   * explicitly joined it. Membership is tracked so it survives a reconnect (rooms
   * are server side state and are lost with the connection) and so a surface can
   * tell whether it still holds a subscription when it tears down.
   */
  public static joinSipAudio({ agencyId, correlationId }: IConveySocketOpts) {
    // Both bails below leave the caller with a player that looks armed and stays
    // silent forever, so neither may pass unreported.
    if (!agencyId || !correlationId) {
      console.warn(
        `[ConveySocket:joinSipAudio] cannot listen: incomplete call target ` +
          `(agencyId=${agencyId}, correlationId=${correlationId})`
      );
      return;
    }

    ConveySocket.JOINED_SIP_AUDIO.add(ConveySocket.getSipAudio({ agencyId, correlationId }));
    if (!ConveySocket.socketManager) {
      console.warn(
        `[ConveySocket:joinSipAudio] socket not connected yet; queued join for ` +
          `${ConveySocket.getSipAudio({ agencyId, correlationId })} until reconnect`
      );
      return;
    }
    ConveySocket.socketManager.emit(ConveySocket.JOIN_SIP_AUDIO_CMD, { agencyId, correlationId });
  }

  public static leaveSipAudio({ agencyId, correlationId }: IConveySocketOpts) {
    if (!agencyId || !correlationId) return;

    ConveySocket.JOINED_SIP_AUDIO.delete(ConveySocket.getSipAudio({ agencyId, correlationId }));
    if (!ConveySocket.socketManager) return;
    ConveySocket.socketManager.emit(ConveySocket.LEAVE_SIP_AUDIO_CMD, { agencyId, correlationId });
  }

  public static isSipAudioJoined({ agencyId, correlationId }: IConveySocketOpts) {
    if (!agencyId || !correlationId) return false;
    return ConveySocket.JOINED_SIP_AUDIO.has(ConveySocket.getSipAudio({ agencyId, correlationId }));
  }

  private static rejoinSipAudio() {
    ConveySocket.JOINED_SIP_AUDIO.forEach((channel) => {
      const parsed = parseSipChannelEvent(channel);
      if (!parsed || !ConveySocket.socketManager) return;
      ConveySocket.socketManager.emit(ConveySocket.JOIN_SIP_AUDIO_CMD, {
        agencyId: parsed.agencyId,
        correlationId: parsed.correlationId
      });
    });
  }

  static getInstance() {
    if (!ConveySocket.instance) {
      ConveySocket.instance = new ConveySocket();
    }
    return ConveySocket.instance;
  }

  static getSipRoom({ agencyId, correlationId }: IConveySocketOpts) {
    return `sip:${agencyId}:${correlationId}`;
  }

  static getSipSentiment({ agencyId, correlationId }: IConveySocketOpts) {
    return `sipSentiment:${agencyId}:${correlationId}`;
  }

  static getSipAudio({ agencyId, correlationId }: IConveySocketOpts) {
    return `sipAudio:${agencyId}:${correlationId}`;
  }

  static getSipStable({ agencyId, correlationId }: IConveySocketOpts) {
    return `sip:stable:${agencyId}:${correlationId}`;
  }

  listen(eventName: string, listener: (data?: any) => void, force?: boolean): ConveySocket {
    if (this.listeners(eventName).length > 0 && !force) {
      return this;
    } else {
      this.on(eventName, listener);
      return this;
    }
  }

  listenOff(eventName: string, listener: (data?: any) => void): ConveySocket {
    if (this.listeners(eventName).length > 0) {
      this.off(eventName, listener);
      return this;
    } else {
      return this;
    }
  }
}
